import React from "react";
import {
  type ConsultNoteMissingLabExtractionWarning,
  type ConsultNoteMultipleIntakesWarning,
  type ConsultNotePendingAction,
  type ConsultNoteRevisionCompareResponse,
  type ConsultNoteRevisionSummary,
  type ConsultNoteWorkspace,
} from "./types";
import { appBadgeClasses, joinClasses } from "./utils";

type DiffPart = {
  text: string;
  type: "equal" | "added" | "removed";
};

function tokenizeDiffText(value: string) {
  return value.match(/(\s+|[^\s]+)/g) || [];
}

function buildWordDiff(from: string, to: string) {
  const fromTokens = tokenizeDiffText(from);
  const toTokens = tokenizeDiffText(to);
  const rowCount = fromTokens.length;
  const columnCount = toTokens.length;
  const dp = Array.from({ length: rowCount + 1 }, () => Array<number>(columnCount + 1).fill(0));

  for (let row = rowCount - 1; row >= 0; row -= 1) {
    for (let column = columnCount - 1; column >= 0; column -= 1) {
      dp[row][column] =
        fromTokens[row] === toTokens[column]
          ? dp[row + 1][column + 1] + 1
          : Math.max(dp[row + 1][column], dp[row][column + 1]);
    }
  }

  const fromParts: DiffPart[] = [];
  const toParts: DiffPart[] = [];
  let row = 0;
  let column = 0;

  while (row < rowCount && column < columnCount) {
    if (fromTokens[row] === toTokens[column]) {
      fromParts.push({ text: fromTokens[row], type: "equal" });
      toParts.push({ text: toTokens[column], type: "equal" });
      row += 1;
      column += 1;
      continue;
    }

    if (dp[row + 1][column] >= dp[row][column + 1]) {
      fromParts.push({ text: fromTokens[row], type: "removed" });
      row += 1;
      continue;
    }

    toParts.push({ text: toTokens[column], type: "added" });
    column += 1;
  }

  while (row < rowCount) {
    fromParts.push({ text: fromTokens[row], type: "removed" });
    row += 1;
  }

  while (column < columnCount) {
    toParts.push({ text: toTokens[column], type: "added" });
    column += 1;
  }

  return { fromParts, toParts };
}

function DiffText({
  label,
  value,
  parts,
}: {
  label: string;
  value: string;
  parts: DiffPart[];
}) {
  if (!value) {
    return (
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">{label}</p>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Empty</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className="whitespace-pre-wrap text-sm leading-6 text-neutral-800 dark:text-neutral-200">
        {parts.map((part, index) => {
          if (part.type === "equal") return <React.Fragment key={`${part.type}-${index}`}>{part.text}</React.Fragment>;

          return (
            <mark
              key={`${part.type}-${index}`}
              className={joinClasses(
                "rounded px-0.5",
                part.type === "added"
                  ? "bg-emerald-100 text-emerald-950 dark:bg-emerald-900/50 dark:text-emerald-100"
                  : "bg-rose-100 text-rose-950 line-through dark:bg-rose-900/50 dark:text-rose-100"
              )}
            >
              {part.text}
            </mark>
          );
        })}
      </p>
    </div>
  );
}

interface WorkspaceHeaderProps {
  workspace: ConsultNoteWorkspace;
  noteTitle: string;
  isMutating: boolean;
  pendingAction: ConsultNotePendingAction | null;
  copyState: "idle" | "copied" | "failed";
  formId: string;
  onTitleChange: (value: string) => void;
  onCopyNote: () => void;
  onDuplicate: () => void;
  onOpenHistory: () => void;
  showHistoryButton: boolean;
  onSign: () => void;
  onRegenerate: () => void;
}

export function WorkspaceHeader({
  workspace,
  noteTitle,
  isMutating,
  pendingAction,
  copyState,
  formId,
  onTitleChange,
  onCopyNote,
  onDuplicate,
  onOpenHistory,
  showHistoryButton,
  onSign,
  onRegenerate,
}: WorkspaceHeaderProps) {
  const actionMenuItemClasses =
    "flex w-full items-center rounded-md px-3 py-2 text-left text-sm font-medium text-neutral-700 transition hover:bg-neutral-100 hover:text-neutral-950 dark:text-neutral-200 dark:hover:bg-neutral-900 dark:hover:text-white";
  const actionMenuButtonClasses = joinClasses(
    actionMenuItemClasses,
    isMutating && "cursor-not-allowed opacity-60 hover:bg-transparent hover:text-inherit"
  );
  const noteMetaItems = [
    workspace.note.statusLabel,
    workspace.note.generationModel ? `Generated with ${workspace.note.generationModel}` : "Generation model not recorded",
    `Tebra ${workspace.note.tebraExportStatusLabel}`,
    `Created ${workspace.note.createdAtLabel}`,
    `Last updated ${workspace.note.updatedAtLabel}`,
    `Signed at ${workspace.note.finalizedAtLabel || "Not signed"}`,
  ];

  function closeActionsMenu(element: EventTarget | null) {
    if (!(element instanceof HTMLElement)) return;

    element.closest("details[data-dismissible-details='true']")?.removeAttribute("open");
  }

  return (
    <section className="app-surface p-4">
      <div className="flex flex-col gap-4">
        <div className="min-w-0 flex-1 space-y-3 lg:max-w-3xl">
          <div>
            <div className="flex flex-wrap items-stretch gap-3">
              <div className="min-w-0 flex-1">
                {workspace.note.readOnly ? (
                  <div className="flex h-10 items-center">
                    <h2 className="truncate text-2xl font-semibold text-neutral-950 dark:text-white">{noteTitle}</h2>
                  </div>
                ) : (
                  <label className="block">
                    <span className="sr-only">Consult note title</span>
                    <input
                      type="text"
                      value={noteTitle}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => onTitleChange(event.target.value)}
                      disabled={isMutating}
                      className="app-input !mt-0 h-10 text-xl font-semibold"
                      placeholder="Consult note title"
                    />
                  </label>
                )}
              </div>

              <FormActions
                note={workspace.note}
                isMutating={isMutating}
                pendingAction={pendingAction}
                formId={formId}
                showReadOnlyMessage={false}
                onSign={onSign}
              />

              <button type="button" className="app-button-secondary h-10 whitespace-nowrap px-3" onClick={onCopyNote}>
                {copyState === "copied" ? "Copied" : copyState === "failed" ? "Copy failed" : "Copy note"}
              </button>

              <details className="group relative shrink-0" data-dismissible-details="true">
                <summary className="app-button-secondary flex h-10 cursor-pointer list-none items-center gap-2 whitespace-nowrap px-3 marker:hidden [&::-webkit-details-marker]:hidden">
                  <span>Actions</span>
                  <span className="text-neutral-500 transition group-open:rotate-180 dark:text-neutral-400">⌄</span>
                </summary>

                <div className="absolute right-0 z-20 mt-2 w-56 rounded-lg border border-neutral-200 bg-white p-1.5 shadow-lg dark:border-neutral-800 dark:bg-neutral-950">
                  {workspace.paths.duplicate && !workspace.note.readOnly && showHistoryButton ? (
                    <button
                      type="button"
                      className={actionMenuButtonClasses}
                      disabled={isMutating}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        closeActionsMenu(event.currentTarget);
                        onDuplicate();
                      }}
                    >
                      {pendingAction === "duplicate" ? "Duplicating..." : "Duplicate to new note"}
                    </button>
                  ) : null}
                  <a href={workspace.paths.patient} className={actionMenuItemClasses} target="_blank" rel="noopener noreferrer">
                    View Patient
                  </a>

                  {workspace.paths.facesheetConsultNote ? (
                    <a
                      href={workspace.paths.facesheetConsultNote}
                      className={actionMenuItemClasses}
                      data-open-in-window="true"
                    >
                      Facesheet consult note
                    </a>
                  ) : null}

                  <a
                    href={workspace.paths.previewPdf}
                    className={actionMenuItemClasses}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Preview PDF
                  </a>

                  {workspace.paths.compareAiModels ? (
                    <a href={workspace.paths.compareAiModels} className={actionMenuItemClasses}>
                      Compare AI models
                    </a>
                  ) : null}

                  {showHistoryButton ? (
                    <button
                      type="button"
                      className={actionMenuButtonClasses}
                      disabled={isMutating}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        closeActionsMenu(event.currentTarget);
                        onOpenHistory();
                      }}
                    >
                      History
                    </button>
                  ) : null}

                  {!workspace.note.readOnly && !workspace.note.finalized ? (
                    <button
                      type="button"
                      className={actionMenuButtonClasses}
                      disabled={isMutating}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        closeActionsMenu(event.currentTarget);
                        onRegenerate();
                      }}
                    >
                      {pendingAction === "regenerate" ? "Regenerating..." : "Regenerate"}
                    </button>
                  ) : null}
                </div>
              </details>
            </div>
          </div>
          {workspace.note.finalized && !workspace.note.readOnly ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              This note is finalized. Saving again will convert it back to an unsigned draft.
            </p>
          ) : null}
        </div>
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-neutral-500 dark:text-neutral-400">
          {noteMetaItems.map((item, index) => (
            <React.Fragment key={item}>
              {index > 0 ? <span aria-hidden="true">•</span> : null}
              <span>{item}</span>
            </React.Fragment>
          ))}
        </p>
      </div>
    </section>
  );
}

export function TebraExportFailureNotice({
  note,
  disabled,
  pending,
  onRetry,
}: {
  note: ConsultNoteWorkspace["note"];
  disabled: boolean;
  pending: boolean;
  onRetry: () => void;
}) {
  if (note.tebraExportStatus !== "failed") return null;

  return (
    <section className="rounded-xl border border-rose-200/80 bg-rose-50/80 p-4 text-rose-950 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-100">
      <p className="text-sm font-semibold uppercase tracking-[0.14em] text-rose-700 dark:text-rose-300">
        Tebra submission failed
      </p>
      {note.tebraExportError ? <p className="mt-2 text-sm leading-6">{note.tebraExportError}</p> : null}
      {note.canRetryTebraExport ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className="app-button-secondary" onClick={onRetry} disabled={disabled}>
            {pending ? "Retrying..." : "Retry submission"}
          </button>
        </div>
      ) : null}
    </section>
  );
}

export function MultipleConsultIntakesNotice({ warning }: { warning?: ConsultNoteMultipleIntakesWarning }) {
  if (!warning) return null;

  return (
    <section aria-label="Multiple consult intakes" className="rounded-xl border border-amber-200/80 bg-amber-50/80 p-4 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100">
      <h2 className="text-sm font-semibold">{warning.submissions.length} New Patient Consult forms exist</h2>
      <p className="mt-2 text-sm leading-6">
        {warning.submissions.some((submission) => submission.usedForNote)
          ? "The form used to generate this note is marked below."
          : "This note does not identify which of these forms was used."}
      </p>
      <ul className="mt-3 space-y-2 text-sm">
        {warning.submissions.map((submission) => (
          <li key={submission.id} className="flex flex-wrap items-center gap-2">
            <a href={submission.openUrl} target="_blank" rel="noopener noreferrer" className="font-medium underline underline-offset-2">
              New Patient Consult — {submission.submittedAtLabel}
            </a>
            {submission.usedForNote ? <span className={appBadgeClasses("neutral")}>Used for this note</span> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function MissingLabExtractionNotice({
  warning,
  disabled,
  pending,
  onExtractAll,
}: {
  warning?: ConsultNoteMissingLabExtractionWarning;
  disabled: boolean;
  pending: boolean;
  onExtractAll: () => void;
}) {
  if (!warning) return null;

  return (
    <section className="rounded-xl border border-amber-200/80 bg-amber-50/80 p-4 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100">
      <p className="text-sm font-semibold uppercase tracking-[0.14em] text-amber-700 dark:text-amber-300">
        Missing lab extraction
      </p>
      <p className="mt-2 text-sm leading-6">{warning.message}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className="app-button-secondary" onClick={onExtractAll} disabled={disabled}>
          {pending ? "Queueing..." : "Extract Missing Labs"}
        </button>
        {warning.documents.map((document) => (
          <a
            key={document.id}
            href={document.openUrl}
            className="app-button-secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            {document.title}
          </a>
        ))}
      </div>
    </section>
  );
}

export function AiTracePanel({ aiTrace }: { aiTrace: ConsultNoteWorkspace["aiTrace"] }) {
  if (!aiTrace) return null;

  return (
    <details id="consult-note-ai-trace" className="app-surface group min-w-0">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4">
        <div>
          <h2 className="text-lg font-semibold text-neutral-950 dark:text-white">AI Trace</h2>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Stored AI input and structured output for this note version.
          </p>
        </div>
        <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 group-open:text-emerald-800 dark:text-emerald-300 dark:group-open:text-emerald-200">
          Expand
          <span className="inline-flex transition-transform duration-200 group-open:rotate-180">⌄</span>
        </span>
      </summary>

      <div className="border-t border-neutral-200/80 px-4 pb-4 pt-4 dark:border-neutral-800">
        <div className="flex flex-wrap items-center gap-2">
          {aiTrace.provider ? <span className={appBadgeClasses("neutral")}>{aiTrace.provider}</span> : null}
          {aiTrace.model ? <span className={appBadgeClasses("neutral")}>{aiTrace.model}</span> : null}
          {aiTrace.responseId ? <span className={appBadgeClasses("neutral")}>Response {aiTrace.responseId}</span> : null}
          {aiTrace.inputSubmissionId ? (
            <span className={appBadgeClasses("neutral")}>Submission {aiTrace.inputSubmissionId}</span>
          ) : null}
          {aiTrace.blankedSections?.length ? (
            <span className={appBadgeClasses("amber")}>Blanked: {aiTrace.blankedSections.join(", ")}</span>
          ) : null}
        </div>

        <div className="mt-4 grid gap-4 xl:grid-cols-2">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
              AI Input
            </h3>
            <div className="mt-2 max-w-full overflow-x-auto rounded-2xl border border-neutral-200/80 bg-neutral-950 dark:border-neutral-800">
              <pre className="min-w-0 p-4 text-xs leading-6 text-neutral-100">{aiTrace.inputPayloadFormatted || "{}"}</pre>
            </div>
          </div>

          <div className="min-w-0">
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
              AI Output
            </h3>
            <div className="mt-2 max-w-full overflow-x-auto rounded-2xl border border-neutral-200/80 bg-neutral-950 dark:border-neutral-800">
              <pre className="min-w-0 p-4 text-xs leading-6 text-neutral-100">{aiTrace.outputDataFormatted || "{}"}</pre>
            </div>
          </div>
        </div>

        {aiTrace.errorFormatted ? (
          <div className="mt-4">
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">AI Error</h3>
            <div className="mt-2 max-w-full overflow-x-auto rounded-2xl border border-amber-200/80 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/40">
              <pre className="min-w-0 text-xs leading-6 text-amber-950 dark:text-amber-100">{aiTrace.errorFormatted}</pre>
            </div>
          </div>
        ) : null}
      </div>
    </details>
  );
}

export function RevisionHistoryPanel({
  revisions,
  selectedRevision,
  loadingRevisionId,
  onBack,
  onSelectRevision,
}: {
  revisions: ConsultNoteRevisionSummary[];
  selectedRevision: ConsultNoteRevisionCompareResponse | null;
  loadingRevisionId: number | null;
  onBack: () => void;
  onSelectRevision: (revisionId: number) => void;
}) {
  return (
    <section className="app-surface p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-neutral-950 dark:text-white">Revision History</h2>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Review previous consult note revisions and compare them against the immediately prior revision.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="app-button-secondary" onClick={onBack}>
            Back to Note
          </button>
        </div>
      </div>

      {revisions.length === 0 ? (
        <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">No revision history recorded yet.</p>
      ) : (
        <div className="mt-4 grid gap-4 xl:grid-cols-[18rem_minmax(0,1fr)]">
          <div className="space-y-2">
            {revisions.map((revision) => {
              const selected = selectedRevision?.revision.id === revision.id;
              const loading = loadingRevisionId === revision.id;

              return (
                <button
                  key={revision.id}
                  type="button"
                  onClick={() => onSelectRevision(revision.id)}
                  className={joinClasses(
                    "w-full rounded-xl border p-3 text-left transition",
                    selected
                      ? "border-emerald-300 bg-emerald-50/80 dark:border-emerald-800 dark:bg-emerald-950/30"
                      : "border-neutral-200/80 bg-white dark:border-neutral-800 dark:bg-neutral-950/60"
                  )}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-neutral-950 dark:text-white">Revision {revision.revisionNumber}</p>
                    {revision.current ? <span className={appBadgeClasses("emerald")}>Current</span> : null}
                  </div>
                  <p className="mt-1 text-sm text-neutral-700 dark:text-neutral-200">{revision.eventLabel}</p>
                  <p className="mt-2 text-xs uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                    {revision.actorName} • {loading ? "Loading..." : revision.createdAtLabel}
                  </p>
                </button>
              );
            })}
          </div>

          <div className="min-w-0">
            {selectedRevision ? (
              selectedRevision.previousRevision ? (
                  selectedRevision.diff.note.length === 0 &&
                  selectedRevision.diff.visit.length === 0 &&
                  selectedRevision.diff.sections.length === 0 ? (
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">No field changes from the previous revision.</p>
                  ) : (
                    <div className="space-y-5">
                      {selectedRevision.diff.note.length > 0 ? (
                        <section className="space-y-2">
                          <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                            Note fields
                          </h3>
                          {selectedRevision.diff.note.map((change, index) => (
                            <React.Fragment key={`note-${change.field}`}>
                              {index > 0 ? <hr className="border-neutral-200/80 dark:border-neutral-800" /> : null}
                              <div className="space-y-2">
                                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{change.label}</p>
                                {(() => {
                                  const diff = buildWordDiff(change.from, change.to);

                                  return (
                                    <>
                                      <DiffText label="Previous" value={change.from} parts={diff.fromParts} />
                                      <DiffText label="Selected revision" value={change.to} parts={diff.toParts} />
                                    </>
                                  );
                                })()}
                              </div>
                            </React.Fragment>
                          ))}
                        </section>
                      ) : null}

                      {selectedRevision.diff.visit.length > 0 ? (
                        <section className="space-y-2">
                          <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                            Visit
                          </h3>
                          {selectedRevision.diff.visit.map((change, index) => (
                            <React.Fragment key={`visit-${change.field}`}>
                              {index > 0 ? <hr className="border-neutral-200/80 dark:border-neutral-800" /> : null}
                              <div className="space-y-2">
                                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{change.label}</p>
                                {(() => {
                                  const diff = buildWordDiff(change.from, change.to);

                                  return (
                                    <>
                                      <DiffText label="Previous" value={change.from} parts={diff.fromParts} />
                                      <DiffText label="Selected revision" value={change.to} parts={diff.toParts} />
                                    </>
                                  );
                                })()}
                              </div>
                            </React.Fragment>
                          ))}
                        </section>
                      ) : null}

                      {selectedRevision.diff.sections.length > 0 ? (
                        <section className="space-y-2">
                          <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                            Sections
                          </h3>
                          {selectedRevision.diff.sections.map((change, index) => (
                            <React.Fragment key={change.key}>
                              {index > 0 ? <hr className="border-neutral-200/80 dark:border-neutral-800" /> : null}
                              <div className="space-y-2">
                                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{change.title}</p>
                                {(() => {
                                  const diff = buildWordDiff(change.from, change.to);

                                  return (
                                    <>
                                      <DiffText label="Previous" value={change.from} parts={diff.fromParts} />
                                      <DiffText label="Selected revision" value={change.to} parts={diff.toParts} />
                                    </>
                                  );
                                })()}
                              </div>
                            </React.Fragment>
                          ))}
                        </section>
                      ) : null}
                    </div>
                  )
              ) : (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">This is the first recorded revision.</p>
              )
            ) : (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Select a revision to inspect its snapshot and compare it against the previous revision.
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

interface FormActionsProps {
  note: ConsultNoteWorkspace["note"];
  isMutating: boolean;
  pendingAction: ConsultNotePendingAction | null;
  formId?: string;
  showReadOnlyMessage?: boolean;
  onSign: () => void;
}

export function FormActions({
  note,
  isMutating,
  pendingAction,
  formId,
  showReadOnlyMessage = true,
  onSign,
}: FormActionsProps) {
  return (
    <div className="flex flex-wrap gap-2">
      {note.readOnly ? (
        showReadOnlyMessage ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Open the current draft to continue editing or signing.
          </p>
        ) : null
      ) : (
        <>
          <button type="submit" className="app-button-primary" form={formId} disabled={isMutating}>
            {pendingAction === "save" ? "Saving..." : note.finalized ? "Create Updated Draft" : "Save Draft"}
          </button>
          {note.canSign && !note.finalized ? (
            <button type="button" className="app-button-secondary" onClick={onSign} disabled={isMutating}>
              {pendingAction === "sign" ? "Signing..." : "Sign Note"}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
